// Custom purchase paths share one paid-add operation and one checkout handoff.
// Prestige still owns its product forms, drawers, section rendering and public events.
(() => {
  if (window.dfCartActions) return;
  let pending = null;
  let uncertain = null;
  let checkingOut = false;
  let quantityRecovery = null;
  const expressPurchases = new Map();
  const nativeOperations = new Map();
  const nativeUncertain = () => [...nativeOperations.values()].some(operation => operation.uncertain);
  const root = () => window.Shopify.routes.root;
  const drawer = () => document.querySelector('cart-drawer');
  const sectionId = () => document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"]')?.id.replace('shopify-section-', '');
  const message = key => drawer()?.dataset[key] || ({ dfRequestErrorText: 'Please try again.', dfReviewCartText: 'Check your cart before adding again.', dfViewCartText: 'View cart', dfRetryText: 'Retry', dfWaitText: 'Please wait for your cart to update.', dfSelectionChangedText: 'Your selection changed after the add. Check your cart before continuing.' })[key] || '';
  const reviewError = () => Object.assign(new Error(message('dfReviewCartText')), { review: true });

  async function request(path, body) {
    let response;
    try { response = await fetch(root() + path, body ? {
      method: 'POST', headers: body instanceof FormData ? {} : { 'Content-Type': 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body), signal: AbortSignal.timeout(15000)
    } : { signal: AbortSignal.timeout(15000) }); } catch { throw new Error(message('dfRequestErrorText')); }
    let data;
    try { data = await response.json(); } catch { throw Object.assign(new Error(message('dfRequestErrorText')), { rejected: response.status >= 400 && response.status < 500 }); }
    if (!response.ok) throw Object.assign(new Error(data.description || message('dfRequestErrorText')), { rejected: response.status >= 400 && response.status < 500 });
    return data;
  }

  function report(error, host = drawer(), retry, kind = 'purchase') {
    if (!host) return;
    host.querySelector(`[data-df-action-error="${kind}"]`)?.remove();
    const box = document.createElement('p');
    box.dataset.dfActionError = kind;
    box.className = 'df-cart__error text-sm';
    box.setAttribute('role', 'alert');
    box.textContent = error.message || message('dfRequestErrorText');
    if (error.review) {
      const link = document.createElement('a');
      link.href = root() + 'cart'; link.className = 'link'; link.textContent = message('dfViewCartText');
      box.append(' ', link);
    } else if (retry) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'link'; button.textContent = message('dfRetryText');
      button.addEventListener('click', retry); box.append(' ', button);
    }
    host.prepend(box);
  }

  // Compare quantities AND line identity. A different property/selling plan is not this add.
  const paidItem = item => !item.properties?._gift && !window.dfCartAdapter?.isAutomatic(item);
  const identity = item => JSON.stringify([String(item.id), String(item.selling_plan || item.selling_plan_allocation?.selling_plan?.id || ''), Object.entries(item.properties || {}).filter(([,v]) => v !== '').sort(([a],[b]) => a.localeCompare(b))]);
  function quantities(items) {
    const map = new Map();
    for (const item of items) map.set(identity(item), (map.get(identity(item)) || 0) + Number(item.quantity));
    return map;
  }
  function expectedItems(data) {
    if (!(data instanceof FormData)) return data.items;
    const items = new Map();
    for (const [name, value] of data) {
      const match = /^(?:items\[(\d+)\]\[([^\]]+)\]|(id|quantity|selling_plan|properties))(?:\[([^\]]+)\])?$/.exec(name);
      if (!match) continue;
      if (value instanceof File) return null; // A server-generated file URL cannot be compared safely.
      const index = match[1] || '0', field = match[2] || match[3];
      const item = items.get(index) || { quantity: 1, properties: {} };
      if (field === 'properties') item.properties[match[4]] = value;
      else item[field] = value;
      items.set(index, item);
    }
    return [...items.values()];
  }
  const selectionKey = items => items && JSON.stringify([...quantities(items)].sort(([a], [b]) => a.localeCompare(b)));
  async function recover() {
    if (!uncertain) return null;
    const cart = await request('cart.js');
    if (!uncertain.items) throw reviewError();
    const actual = quantities(cart.items.filter(paidItem));
    const baseline = quantities(uncertain.before.items.filter(paidItem).concat(uncertain.items));
    if (actual.size !== baseline.size || [...actual].some(([key, qty]) => baseline.get(key) !== qty)) throw reviewError();
    const section = sectionId();
    if (section) {
      const response = await fetch(`${root()}?section_id=${encodeURIComponent(section)}`);
      if (!response.ok) throw reviewError();
      cart.sections = { [section]: await response.text() };
    } else cart.sections = {};
    uncertain = null;
    return cart;
  }
  function publish(cart, form, items = [], openDrawer = true) {
    const onSuccessDo = openDrawer ? 'force_open_drawer' : undefined;
    form?.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart, onSuccessDo } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: openDrawer ? 'variant:add' : 'df:express-add', cart, onSuccessDo } }));
  }
  function add({ form, variantId, items, openDrawer = true }) {
    if (quantityRecovery || nativeUncertain()) return Promise.reject(reviewError());
    if (pending || checkingOut || nativeOperations.size) return Promise.reject(new Error(message('dfWaitText')));
    const body = form ? new FormData(form) : { items };
    if (form && variantId) body.set(body.has('items[0][id]') ? 'items[0][id]' : 'id', String(variantId));
    const sections = []; document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    if (!sections.length && sectionId()) sections.push(sectionId());
    if (form) { body.set('sections', sections.join(',')); body.set('sections_url', location.pathname); }
    else body.sections = sections.join(',');
    pending = (async () => {
      if (uncertain) {
        const selection = selectionKey(uncertain.items);
        const cart = await recover(); publish(cart, form, [], openDrawer);
        // This click verifies the previous request; it must never replay the paid add.
        return { cart, recovered: true, items: [], selection };
      }
      const before = await request('cart.js');
      const attempted = { before, items: expectedItems(body) };
      let sent = false, accepted = false;
      try {
        sent = true;
        const added = await request('cart/add.js', body);
        accepted = true;
        const cart = await request('cart.js');
        cart.sections = added.sections || {};
        const section = sectionId();
        if (section && !cart.sections[section]) {
          const response = await fetch(`${root()}?section_id=${encodeURIComponent(section)}`);
          if (!response.ok) throw new Error(message('dfRequestErrorText'));
          cart.sections[section] = await response.text();
        }
        const addedItems = added.items || [added];
        publish(cart, form, addedItems, openDrawer);
        return { cart, items: addedItems, selection: selectionKey(attempted.items) };
      } catch (error) {
        if (!sent) throw error;
        if (error.rejected && !accepted) {
          // Shopify can return 422 after adding the available portion of a quantity.
          // A refusal is retryable only when a fresh cart proves no paid lines changed.
          let unchanged = false;
          try {
            const after = await request('cart.js');
            const prior = quantities(before.items.filter(paidItem));
            const actual = quantities(after.items.filter(paidItem));
            unchanged = prior.size === actual.size && [...prior].every(([key, qty]) => actual.get(key) === qty);
          } catch { /* Keep the add uncertain when verification is unavailable. */ }
          if (unchanged) throw error;
        }
        uncertain = attempted;
        try {
          const cart = await recover(); publish(cart, form, [], openDrawer);
          return { cart, recovered: true, items: [], selection: selectionKey(attempted.items) };
        } catch { throw reviewError(); }
      }
    })().finally(() => { pending = null; });
    return pending;
  }
  async function wait() {
    if (pending) await pending;
    if (nativeOperations.size) await Promise.all([...nativeOperations.values()].map(operation => operation.promise));
    if (quantityRecovery || nativeUncertain()) throw reviewError();
    if (uncertain) { const cart = await recover(); publish(cart); }
  }
  async function checkout(submit, host, { express = false, withoutGift = false } = {}) {
    if (checkingOut) return;
    checkingOut = true;
    if (pending || nativeOperations.size) report(Object.assign(new Error(message('dfWaitText')), { review: true }), host);
    try {
      await wait();
      try {
        if (window.dfCartAdapter) await window.dfCartAdapter.sync();
        else await window.dfCartGift?.sync({ checkout: true });
      } catch (error) {
        // Only an explicit, confirmed gift refusal may be bypassed by the customer.
        if (!withoutGift || !error.unavailable) throw error;
      }
      host?.querySelector('[data-df-action-error]')?.remove();
      await submit();
      return true;
    } catch (error) {
      if (express) throw error;
      report(error.review || error.gift || error.quantityRetry ? error : new Error(message('dfRequestErrorText')), host, error.quantityRetry ? error.retry : () => checkout(submit, host), error.gift ? 'gift' : 'purchase');
    } finally { checkingOut = false; }
  }
  async function oneClick(button, { withoutGift = false } = {}) {
    const form = document.getElementById(button.dataset.productForm);
    if (!form || button.disabled || pending || checkingOut || !form.reportValidity()) return;
    const host = button.closest('buy-buttons') || form;
    host.querySelectorAll('[data-df-action-error]').forEach(node => node.remove());
    const originalContent = [...button.childNodes];
    const label = document.createElement('span');
    label.textContent = button.dataset.addingText;
    const spinner = document.createElement('span');
    spinner.className = 'df-express-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    button.replaceChildren(spinner, label);
    button.classList.add('df-express-loading');
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    let leaving = false;
    try {
      const id = form.getAttribute('id');
      const selection = selectionKey(expectedItems(new FormData(form)));
      const previous = expressPurchases.get(id);
      // A retry can reuse only this exact purchase, still present in the server cart.
      // Changing model/accessories after an accepted add requires reviewing that cart;
      // neither silently buy the old selection nor append a second paid purchase.
      if (previous && (!selection || previous.selection !== selection)) throw Object.assign(new Error(message('dfSelectionChangedText')), { review: true });
      if (previous) {
        const cart = await request('cart.js');
        if (selectionKey(cart.items.filter(paidItem)) !== selectionKey(previous.cart.items.filter(paidItem))) throw reviewError();
      }
      const result = previous || await add({ form, openDrawer: false });
      if (!selection || result.selection !== selection) throw reviewError();
      expressPurchases.set(id, result);
      // Only attributes are updated: do not overwrite a delivery note edited meanwhile.
      await request('cart/update.js', { attributes: { 'Quick Buy Used': 'Yes', 'Quick Buy Product': result.items[0]?.product_title || document.title, 'Quick Buy Date': new Date().toLocaleString('fr-FR') } });
      leaving = await checkout(() => {
        // Storage may be unavailable in private contexts; it must not cancel checkout.
        try { sessionStorage.setItem('lcf-quick-buy-checkout', '1'); } catch { /* optional bfcache marker */ }
        location.assign(root() + 'checkout');
      }, host, { express: true, withoutGift });
    } catch (error) {
      report(error, host, () => oneClick(button), error.gift ? 'gift' : 'purchase');
      // The mobile button lives outside the product form: keep feedback beside the clicked button.
      button.after(host.querySelector('[data-df-action-error]'));
      if (error.unavailable) {
        const continueButton = document.createElement('button');
        continueButton.type = 'button'; continueButton.className = 'link';
        continueButton.textContent = message('dfContinueWithoutGiftText');
        continueButton.addEventListener('click', () => oneClick(button, { withoutGift: true }));
        host.querySelector('[data-df-action-error="gift"]').append(' ', continueButton);
      }
    } finally {
      if (!leaving) {
        button.replaceChildren(...originalContent);
        button.classList.remove('df-express-loading');
        button.disabled = false; button.removeAttribute('aria-busy');
      }
    }
  }
  // Observe Prestige's public completion events without rewriting its forms or fetch.
  // A missing completion never unlocks another mutation based on a timer or drawer refresh.
  function watchNative(owner) {
    if (nativeOperations.has(owner)) return;
    let resolve;
    const promise = new Promise(done => { resolve = done; });
    const operation = { promise, resolve, uncertain: false };
    operation.timer = setTimeout(() => {
      operation.uncertain = true;
      resolve();
      report(reviewError(), owner.closest('.look-card') || drawer());
    }, 15000);
    nativeOperations.set(owner, operation);
  }
  function finishNative(owner) {
    clearTimeout(nativeOperations.get(owner)?.timer);
    nativeOperations.get(owner)?.resolve(); nativeOperations.delete(owner);
  }
  document.addEventListener('variant:add', event => finishNative(event.target), true);
  document.addEventListener('cart:error', event => finishNative(event.target), true);

  function quantityHost() {
    return window.themeVariables?.settings.pageType === 'cart' ? document.querySelector('main') : drawer();
  }
  async function renderQuantity(cart) {
    const section = sectionId();
    if (section && !cart.sections?.[section]) {
      const response = await fetch(`${root()}?section_id=${encodeURIComponent(section)}`, { signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw reviewError();
      cart.sections = { [section]: await response.text() };
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true, detail: { baseEvent: 'line-item:change', cart }
    }));
    if (window.themeVariables?.settings.pageType === 'cart' && !checkingOut) location.reload();
  }
  function changeQuantity(control, key, quantity, retry = false) {
    if (pending || checkingOut || uncertain || nativeOperations.size || (quantityRecovery && !retry)) {
      report(uncertain || quantityRecovery || nativeUncertain() ? reviewError() : new Error(message('dfWaitText')), quantityHost());
      control.querySelector('quantity-selector')?.restoreDefaultValue?.();
      return;
    }
    control.setAttribute('aria-busy', 'true');
    pending = (async () => {
      const before = await request('cart.js');
      const item = before.items.find(item => item.key === key);
      if (!item) throw reviewError();
      const operation = { key, quantity, identity: identity(item) };
      quantityRecovery = operation;
      try {
        const sections = []; document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        if (!sections.length && sectionId()) sections.push(sectionId());
        const cart = await request('cart/change.js', { id: key, quantity, sections: sections.join(',') });
        await renderQuantity(cart);
        quantityRecovery = null;
      } catch (error) {
        let cart;
        try { cart = await request('cart.js'); } catch { throw reviewError(); }
        const actual = quantities(cart.items).get(operation.identity) || 0;
        if (actual === quantity) {
          await renderQuantity(cart);
          quantityRecovery = null;
          return;
        }
        // Explicit refusal + unchanged cart has a known result. A lost response does not.
        if (error.rejected && selectionKey(before.items) === selectionKey(cart.items)) {
          quantityRecovery = null;
          throw error;
        }
        // Repeating this absolute quantity for the same key cannot increment twice.
        // Keep other mutations/checkout protected until the target is confirmed.
        throw Object.assign(new Error(message('dfRequestErrorText')), { quantityRetry: true, retry: () => changeQuantity(control, key, quantity, true) });
      }
    })().catch(error => {
      control.querySelector('quantity-selector')?.restoreDefaultValue?.();
      report(error, quantityHost(), error.quantityRetry ? () => changeQuantity(control, key, quantity, true) : undefined);
      throw error;
    }).finally(() => { pending = null; control.removeAttribute('aria-busy'); });
    // This is an event handler; wait()/checkout may also observe the same rejection.
    pending.catch(() => {});
  }
  // Own only cart quantity controls, including desktop full-cart controls outside line-item.
  // This replaces dependence on missing native network-error events; vendor code is untouched.
  function onQuantity(event) {
    const control = event.target.closest?.('line-item-quantity');
    if (!control || !control.closest('cart-drawer, main')) return;
    let key, quantity;
    if (event.type === 'click') {
      const link = event.target.closest('a[href*="/cart/change"]');
      if (!link) return;
      const url = new URL(link.href); key = url.searchParams.get('id'); quantity = Number(url.searchParams.get('quantity'));
    } else {
      if (!event.target.matches('[data-line-key]')) return;
      key = event.target.dataset.lineKey; quantity = Number(event.target.value);
    }
    event.preventDefault(); event.stopImmediatePropagation();
    if (!key || !Number.isInteger(quantity) || quantity < 0) return;
    changeQuantity(control, key, quantity);
  }
  window.addEventListener('click', onQuantity, true);
  window.addEventListener('change', onQuantity, true);
  const allowedCheckout = new WeakSet();
  const preparedCheckoutForms = new WeakSet();
  document.addEventListener('formdata', event => {
    if (!preparedCheckoutForms.has(event.target)) return;
    // Quantity controls have already committed through Prestige before wait() resolves.
    // Gift synchronization can reorder cart lines: never submit the old positional
    // updates[] vector over the fresh server cart. Notes/attributes/submitter are retained.
    for (const key of [...event.formData.keys()]) {
      if (key === 'updates' || key.startsWith('updates[')) event.formData.delete(key);
    }
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (/\/cart\/add/.test(form.action) && (pending || checkingOut || uncertain || quantityRecovery || nativeOperations.size)) {
      event.preventDefault(); event.stopImmediatePropagation();
      report(uncertain || quantityRecovery || nativeUncertain() ? reviewError() : new Error(message('dfWaitText'))); return;
    }
    if (/\/cart\/add/.test(form.action) && form.closest('product-form') && form.checkValidity()) {
      watchNative(form);
      return;
    }
    if (event.submitter?.name !== 'checkout' || !/\/cart\/?$/.test(new URL(form.action).pathname)) return;
    if (allowedCheckout.has(form)) { allowedCheckout.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    startCheckout(form, event.submitter);
  }, true);
  function startCheckout(form, submitter) {
    const fullCart = Boolean(form.closest('.shopify-section--main-cart'));
    checkout(() => {
      // Section updates can replace the form. Preserve the original button and form data.
      const current = form.isConnected ? form : (fullCart ? document.querySelector('.shopify-section--main-cart form.cart-page') : drawer()?.querySelector('form[action]'));
      const button = submitter.isConnected ? submitter : current?.querySelector(submitter.classList.contains('df-cart__scalapay') ? '.df-cart__scalapay' : '[name="checkout"]');
      if (!current || !button) throw new Error(message('dfRequestErrorText'));
      button.removeAttribute('aria-busy'); allowedCheckout.add(current); preparedCheckoutForms.add(current); current.requestSubmit(button);
    }, window.themeVariables?.settings.pageType === 'cart' ? document.querySelector('main') : drawer());
  }
  // CartBot also binds click handlers and can navigate without a submit event.
  // Own the checkout click before those handlers, then submit the same native form once ready.
  window.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    const form = button?.form;
    if (!form || !/\/cart\/?$/.test(new URL(form.action).pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    startCheckout(form, button);
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-pdp-v3-one-click], .quick-buy-button');
    if (!button) return; event.preventDefault(); oneClick(button);
  });
  window.dfCartActions = { add, wait, report, checkout };
})();
